import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useI18n } from '../../i18n/I18nProvider';
import { createChain, createJsonFetch, deleteChain, listChains } from './settings.api';
import type { ChainRule, FieldMapEntry, JsonFetch } from './settings.api';

function useScreenFetch(): JsonFetch {
  const { authorizedFetch } = useAuth();
  return useMemo(() => createJsonFetch(authorizedFetch), [authorizedFetch]);
}

function fieldMapAttribute(fieldMap: readonly FieldMapEntry[]): string {
  return fieldMap.map((entry) => `${entry.from}→${entry.to}`).join(',');
}

function completionAttribute(chain: ChainRule): string {
  const rule = chain.completionRule;
  return rule === undefined ? '' : `${rule.metric}${rule.operator}${rule.value}`;
}

export function DocumentChainsView({
  chains,
  error,
  onDelete,
}: {
  chains: readonly ChainRule[];
  error?: string | null;
  onDelete?: (id: string) => void;
}): JSX.Element {
  const { t } = useI18n();
  return (
    <div className="settings__panel" data-testid="settings-document-chains">
      {error === null || error === undefined ? null : <p className="alert alert--danger">{error}</p>}
      {chains.length === 0 ? <p className="muted">{t('settings.empty')}</p> : null}
      {chains.map((chain) => (
        <article
          key={chain.id}
          className="settings__card"
          data-testid="chain-rule"
          data-field-map={fieldMapAttribute(chain.fieldMap)}
          data-auto-create={chain.autoCreate ? 'true' : 'false'}
          data-completion={completionAttribute(chain)}
        >
          <header className="settings__card-head">
            <strong>
              {chain.sourceType} → {chain.targetType}
            </strong>
            <span className="pill">{chain.qtyMode}</span>
          </header>
          <div className="settings__row">
            <span className="muted">{t('chains.fieldMap')}</span>
            <span className="settings__chips">
              {chain.fieldMap.map((entry) => (
                <span key={`${entry.from}-${entry.to}`} className="chip" data-map-entry={`${entry.from}→${entry.to}`}>
                  <span className="mono">{entry.from}</span> → <span className="mono">{entry.to}</span>
                </span>
              ))}
            </span>
          </div>
          <p className="muted">
            {t('chains.autoCreate')}: {chain.autoCreate ? t('settings.users.yes') : t('settings.users.no')} ·{' '}
            {t('chains.qtyControl')}: {chain.qtyControl ? t('settings.users.yes') : t('settings.users.no')}
          </p>
          {chain.completionRule === undefined ? null : (
            <p className="muted">
              {t('chains.completionRule')}: <span className="mono">{completionAttribute(chain)}</span>
            </p>
          )}
          {onDelete === undefined ? null : (
            <div className="settings__actions">
              <button type="button" className="btn btn--small" onClick={() => onDelete(chain.id)}>
                {t('chains.delete')}
              </button>
            </div>
          )}
        </article>
      ))}
    </div>
  );
}

interface ChainDraft {
  sourceType: string;
  sourceOperation: string;
  targetType: string;
  targetOperation: string;
  fieldMap: string;
  autoCreate: boolean;
  completionMetric: string;
  completionValue: string;
}

const EMPTY_DRAFT: ChainDraft = {
  sourceType: '',
  sourceOperation: '',
  targetType: '',
  targetOperation: '',
  fieldMap: '[{"from":"data.counterparty","to":"data.person"}]',
  autoCreate: false,
  completionMetric: 'remaining_qty',
  completionValue: '0',
};

export default function DocumentChainsScreen(): JSX.Element {
  const { t } = useI18n();
  const fetchJson = useScreenFetch();
  const [chains, setChains] = useState<ChainRule[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<ChainDraft>(EMPTY_DRAFT);
  const [preview, setPreview] = useState<FieldMapEntry[]>([]);

  const reload = useCallback(async (): Promise<void> => {
    try {
      setChains(await listChains(fetchJson));
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }, [fetchJson]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const create = async (): Promise<void> => {
    try {
      let fieldMap: FieldMapEntry[] = [];
      if (draft.fieldMap.trim() !== '') {
        fieldMap = JSON.parse(draft.fieldMap) as FieldMapEntry[];
      }
      setPreview(fieldMap);
      const body: Omit<ChainRule, 'id'> = {
        sourceType: draft.sourceType,
        sourceOperation: draft.sourceOperation,
        targetType: draft.targetType,
        targetOperation: draft.targetOperation,
        sourceStatusCondition: ['posted'],
        fieldMap,
        qtyMode: '1:1',
        qtyControl: true,
        ...(draft.completionValue.trim() === ''
          ? {}
          : {
              completionRule: {
                metric: draft.completionMetric as NonNullable<ChainRule['completionRule']>['metric'],
                operator: '<=' as const,
                value: draft.completionValue,
              },
            }),
        required: false,
        autoCreate: draft.autoCreate,
        activeFrom: new Date().toISOString().slice(0, 10),
        isDisabled: false,
      };
      await createChain(fetchJson, body);
      await reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  };

  const remove = async (id: string): Promise<void> => {
    try {
      await deleteChain(fetchJson, id);
      await reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  };

  if (chains === null) {
    return (
      <section className="card" data-testid="document-chains-loading">
        <p className="muted">{t('settings.loading')}</p>
      </section>
    );
  }

  return (
    <div className="settings__screen">
      <header className="page-head">
        <div>
          <h1>{t('chains.title')}</h1>
          <p className="muted">{t('chains.subtitle')}</p>
        </div>
      </header>

      <DocumentChainsView chains={chains} error={error} onDelete={(id) => void remove(id)} />

      <section className="card" data-testid="chain-editor">
        <h2>{t('chains.newRule')}</h2>
        <div className="settings__form">
          {(['sourceType', 'sourceOperation', 'targetType', 'targetOperation'] as const).map((field) => (
            <label key={field} className="field">
              <span>{t(`chains.${field}`)}</span>
              <input
                className="input"
                value={draft[field]}
                onChange={(event) => setDraft({ ...draft, [field]: event.currentTarget.value })}
              />
            </label>
          ))}
          <label className="field settings__form-wide">
            <span>{t('chains.fieldMap')}</span>
            <textarea
              className="input settings__editor"
              value={draft.fieldMap}
              onChange={(event) => setDraft({ ...draft, fieldMap: event.currentTarget.value })}
            />
          </label>
          <label className="field">
            <span>{t('chains.completionMetric')}</span>
            <select
              className="input"
              value={draft.completionMetric}
              onChange={(event) => setDraft({ ...draft, completionMetric: event.currentTarget.value })}
            >
              <option value="remaining_qty">{t('chains.remainingQty')}</option>
              <option value="remaining_amount">{t('chains.remainingAmount')}</option>
            </select>
          </label>
          <label className="field">
            <span>{t('chains.completionValue')}</span>
            <input
              className="input"
              value={draft.completionValue}
              onChange={(event) => setDraft({ ...draft, completionValue: event.currentTarget.value })}
            />
          </label>
          <label className="settings__row">
            <span>{t('chains.autoCreate')}</span>
            <input
              type="checkbox"
              checked={draft.autoCreate}
              aria-label={t('chains.autoCreate')}
              onChange={(event) => setDraft({ ...draft, autoCreate: event.currentTarget.checked })}
            />
          </label>
        </div>
        <div className="settings__actions">
          <button type="button" className="btn btn--primary" onClick={() => void create()}>
            {t('chains.create')}
          </button>
        </div>
        <div className="settings__chips" data-testid="chain-preview">
          {preview.map((entry) => (
            <span key={`${entry.from}-${entry.to}`} className="chip">
              <span className="mono">{entry.from}</span> → <span className="mono">{entry.to}</span>
            </span>
          ))}
        </div>
      </section>
    </div>
  );
}
